import { subscribe } from '@/api';
import type { CreditPack } from '@/api';

function dollars(cents: number) {
  return `$${(cents / 100).toFixed(0)}`;
}

type Props = {
  packs: CreditPack[];
  busy: string | null;
  err: string | null;
  hostingBusy?: boolean;
  onBuy: (packId: string) => void;
  onHost?: () => void;
};

export default function Pricing({ packs, busy, err, hostingBusy, onBuy, onHost }: Props) {
  const startHost = () => {
    if (onHost) {
      onHost();
      return;
    }
    void subscribe('hosting').then((res) => {
      if (res.url) window.location.assign(res.url);
    }).catch(() => undefined);
  };

  return (
    <section id="pricing" className="py-20 md:py-28 border-t border-border">
      <div className="max-w-[1040px] mx-auto px-6">
        <div className="max-w-[620px]">
          <h2 className="font-heading text-3xl md:text-4xl tracking-tight text-textp font-semibold">Hosting</h2>
          <p className="mt-4 text-texts leading-relaxed">
            New accounts get 20 hosted credits to finish the site. When you are ready to publish, put a card on file. Hosting is monthly. Usage meters on the same invoice.
          </p>
        </div>

        {err && <div className="mt-4 text-sm text-error bg-error/10 border border-error/30 rounded-md px-3 py-2">{err}</div>}

        <div className="mt-10 bg-surface border-2 border-accent rounded-xl p-6 max-w-xl" data-testid="pricing-hosting">
          <div className="text-sm text-texts font-medium">Sourcetape Hosting</div>
          <p className="mt-3 text-sm text-texts leading-relaxed">
            Card required. Isolated Terraform apply for your slug.sourcetape.io after payment. Custom domains get their own hosted zone.
          </p>
          <button
            data-testid="start-hosting"
            disabled={!!hostingBusy}
            onClick={startHost}
            className="whitespace-nowrap mt-6 flex items-center justify-center gap-1.5 text-sm font-medium px-4 py-2.5 rounded-lg bg-accent hover:bg-accent-hover text-accent-text disabled:opacity-60"
          >
            {hostingBusy ? 'Redirecting…' : 'Start hosting'}
          </button>
        </div>

        <div className="mt-14 text-xs uppercase tracking-wider text-texts">Optional credit packs</div>
        <p className="mt-2 text-sm text-texts">Refills if you want more composition runs before you host. Not required to go live.</p>

        <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4 items-stretch" data-testid="pricing-packs">
          {packs.map((p, i) => {
            const featured = i === 1;
            return (
              <div
                key={p.id}
                className={`relative rounded-xl p-6 flex flex-col ${
                  featured ? 'bg-surface border border-border' : 'bg-surface border border-border'
                }`}
              >
                <div className="text-sm text-texts font-medium">{p.name}</div>
                <div className="mt-4 flex items-baseline gap-2">
                  <span className="font-mono text-3xl text-textp">{dollars(p.price_cents)}</span>
                  <span className="font-mono text-xs text-texts">/ {p.credits} credits</span>
                </div>
                <p className="mt-3 text-sm text-texts leading-relaxed flex-1">{p.blurb}</p>
                <button
                  data-testid={`buy-${p.id}`}
                  disabled={busy === p.id}
                  onClick={() => onBuy(p.id)}
                  className="whitespace-nowrap mt-6 w-full flex items-center justify-center gap-1.5 text-sm font-medium px-4 py-2.5 rounded-lg transition-colors disabled:opacity-60 bg-surface2 hover:bg-border text-textp border border-border"
                >
                  {busy === p.id ? 'Redirecting…' : `Buy ${p.name}`}
                </button>
              </div>
            );
          })}
        </div>

        <p className="mt-6 text-xs text-texts">
          Payments are processed by Stripe. Hosting starts after checkout completes. Apply never runs from the success page.
        </p>
      </div>
    </section>
  );
}
